import {
  Delete as UnlinkIcon,
  Download as DownloadIcon,
  LibraryBooks as LibraryIcon,
  Search as SearchIcon,
  UploadFile as UploadIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LogoLoader from "../../common/LogoLoader";
import {
  ACCEPTED_DOCUMENT_EXTENSIONS,
  Document,
  documentsApi,
  EXTRACTION_STATUS_LABELS,
  formatFileSize,
  MAX_DOCUMENT_SIZE_BYTES,
} from "../../../services/documentsApi";

// Reusable upload + listing panel for the org document library (#1013).
// Renders a drag-and-drop zone and the documents linked to ONE target:
// either a research project (reference material) or a track entity
// (per-candidate docs, e.g. RFI responses fed into evaluation).

interface DocumentsPanelProps {
  projectId?: string;
  trackEntityId?: string;
  // When the target is a candidate, the entity's graph id + display name let
  // the library picker surface docs already linked to this company elsewhere
  // (other projects, org-global entity links) as a separate group.
  entityKgId?: string;
  entityName?: string;
  compact?: boolean;
  // false renders a plain button row + list (for modal embedding) instead of
  // the drag-and-drop zone.
  dropZone?: boolean;
}

const EXTRACTION_CHIP_COLOR: Record<
  string,
  "default" | "success" | "warning" | "error"
> = {
  pending: "default",
  done: "success",
  low_quality: "warning",
  unsupported_scanned: "warning",
  failed: "error",
};

const DocumentsPanel: React.FC<DocumentsPanelProps> = ({
  projectId,
  trackEntityId,
  entityKgId,
  entityName,
  compact = false,
  dropZone = true,
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    message: string;
    severity: "success" | "info" | "error";
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Library picker dialog state — select existing org-library documents to
  // link to this target instead of uploading new files.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [libraryDocs, setLibraryDocs] = useState<Document[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [linking, setLinking] = useState(false);
  // Ids of library docs linked to this candidate's company elsewhere.
  const [relatedIds, setRelatedIds] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    try {
      const response = await documentsApi.listDocuments(
        projectId ? { project: projectId } : { trackEntity: trackEntityId },
      );
      setDocuments(response.results);
    } catch {
      setSnackbar({ message: "Failed to load documents", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [projectId, trackEntityId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const uploadFiles = useCallback(
    async (files: FileList | File[]) => {
      setUploading(true);
      try {
        for (const file of Array.from(files)) {
          if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
            setSnackbar({
              message: `${file.name} exceeds the ${formatFileSize(
                MAX_DOCUMENT_SIZE_BYTES,
              )} upload limit`,
              severity: "error",
            });
            continue;
          }
          setUploadProgress(0);
          try {
            const result = await documentsApi.uploadDocument({
              file,
              projectId,
              trackEntityId,
              onUploadProgress: setUploadProgress,
            });
            setSnackbar(
              result.created
                ? { message: `Uploaded ${file.name}`, severity: "success" }
                : {
                    message: `${file.name} was already in your library — linked it here`,
                    severity: "info",
                  },
            );
          } catch (error: any) {
            const status = error?.response?.status;
            const message =
              status === 413
                ? `${file.name} is too large`
                : status === 415
                  ? `${file.name} is not a PDF — only PDF files are supported`
                  : status === 403
                    ? "You don't have permission to upload documents"
                    : status === 503
                      ? "Document storage isn't set up in this environment yet"
                      : `Failed to upload ${file.name}`;
            setSnackbar({ message, severity: "error" });
          }
        }
      } finally {
        setUploading(false);
        setUploadProgress(null);
        refresh();
      }
    },
    [projectId, trackEntityId, refresh],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setDragOver(false);
      if (event.dataTransfer.files.length > 0) {
        uploadFiles(event.dataTransfer.files);
      }
    },
    [uploadFiles],
  );

  const handleUnlink = useCallback(
    async (doc: Document) => {
      const link = doc.links.find((candidate) =>
        projectId
          ? candidate.project === projectId
          : candidate.track_entity === trackEntityId,
      );
      if (!link) return;
      try {
        await documentsApi.deleteLink(link.id);
        setSnackbar({
          message: `Removed ${doc.filename} from here (still in your org library)`,
          severity: "info",
        });
      } catch {
        setSnackbar({ message: "Failed to remove link", severity: "error" });
      }
      refresh();
    },
    [projectId, trackEntityId, refresh],
  );

  const openPicker = useCallback(async () => {
    setPickerOpen(true);
    setLibrarySearch("");
    setSelectedIds(new Set());
    setLibraryLoading(true);
    try {
      // The library list is paginated (50/page); fetch up to 4 pages and
      // rely on the client-side search filter beyond that.
      const docs: Document[] = [];
      let page = 1;
      for (;;) {
        const response = await documentsApi.listDocuments({ page });
        docs.push(...response.results);
        if (!response.next || page >= 4) break;
        page += 1;
      }
      setLibraryDocs(docs);
      if (entityKgId) {
        const related = await documentsApi.listDocuments({
          kgEntity: entityKgId,
        });
        setRelatedIds(new Set(related.results.map((doc) => doc.id)));
      } else {
        setRelatedIds(new Set());
      }
    } catch {
      setSnackbar({ message: "Failed to load library", severity: "error" });
      setPickerOpen(false);
    } finally {
      setLibraryLoading(false);
    }
  }, [entityKgId]);

  // Documents already linked to this target are hidden from the picker.
  const linkedIds = useMemo(
    () => new Set(documents.map((doc) => doc.id)),
    [documents],
  );

  const pickerDocs = useMemo(() => {
    const query = librarySearch.trim().toLowerCase();
    return libraryDocs.filter((doc) => {
      if (linkedIds.has(doc.id)) return false;
      if (!query) return true;
      return (
        doc.filename.toLowerCase().includes(query) ||
        doc.title.toLowerCase().includes(query) ||
        doc.description.toLowerCase().includes(query)
      );
    });
  }, [libraryDocs, linkedIds, librarySearch]);

  // For candidate targets, split the picker into docs already linked to this
  // company elsewhere vs the rest of the org library.
  const pickerGroups = useMemo(() => {
    if (!entityKgId) return [{ label: null, docs: pickerDocs }];
    const related = pickerDocs.filter((doc) => relatedIds.has(doc.id));
    const general = pickerDocs.filter((doc) => !relatedIds.has(doc.id));
    return [
      {
        label: `Linked to ${entityName || "this company"} elsewhere`,
        docs: related,
      },
      { label: "Org library", docs: general },
    ].filter((group) => group.docs.length > 0);
  }, [pickerDocs, relatedIds, entityKgId, entityName]);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const linkSelected = useCallback(async () => {
    setLinking(true);
    let linked = 0;
    let failed = 0;
    for (const id of Array.from(selectedIds)) {
      try {
        await documentsApi.createLink(
          projectId
            ? { document: id, project: projectId }
            : { document: id, track_entity: trackEntityId! },
        );
        linked += 1;
      } catch {
        failed += 1;
      }
    }
    setLinking(false);
    setPickerOpen(false);
    setSnackbar(
      failed > 0
        ? {
            message: `Added ${linked} document${linked === 1 ? "" : "s"}, ${failed} failed`,
            severity: "error",
          }
        : {
            message: `Added ${linked} document${linked === 1 ? "" : "s"} from the library`,
            severity: "success",
          },
    );
    refresh();
  }, [selectedIds, projectId, trackEntityId, refresh]);

  const actionButtons = (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<UploadIcon />}
        disabled={uploading}
        onClick={(event) => {
          event.stopPropagation();
          fileInputRef.current?.click();
        }}
      >
        {uploading ? "Uploading…" : "Upload Files"}
      </Button>
      <Button
        variant="outlined"
        size="small"
        startIcon={<LibraryIcon />}
        disabled={uploading}
        onClick={(event) => {
          event.stopPropagation();
          openPicker();
        }}
      >
        Select from Library
      </Button>
    </>
  );

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        accept={ACCEPTED_DOCUMENT_EXTENSIONS}
        onChange={(event) => {
          if (event.target.files?.length) uploadFiles(event.target.files);
          event.target.value = "";
        }}
      />
      {dropZone ? (
        <Box
          onDragOver={(event) => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: 2,
            borderColor: dragOver ? "primary.main" : "divider",
            borderStyle: "dashed",
            borderRadius: 1,
            p: compact ? 2 : 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            backgroundColor: dragOver ? "action.hover" : "transparent",
            transition: "background-color 120ms, border-color 120ms",
          }}
        >
          <UploadIcon
            sx={{
              fontSize: compact ? 28 : 36,
              color: dragOver ? "primary.main" : "text.disabled",
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Drag & drop PDF files here, or click to browse
          </Typography>
          {!compact && (
            <Typography variant="caption" color="text.disabled">
              {projectId
                ? "PDF only — tender documents, market reports, product specs, or other reference materials"
                : "PDF only — RFI responses, internal research, or other documents about this candidate; they are used during evaluation"}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>{actionButtons}</Box>
          {uploadProgress !== null && (
            <Box sx={{ width: "100%", mt: 1 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
        </Box>
      ) : (
        <Box>
          <Box sx={{ display: "flex", gap: 1 }}>{actionButtons}</Box>
          {uploadProgress !== null && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
        </Box>
      )}

      {loading || documents.length === 0 ? (
        // Loading / empty states: centered in a fixed-height area in the
        // modal variant, an unobtrusive inline row under the drop zone.
        <Box
          sx={
            dropZone
              ? { display: "flex", justifyContent: "center", py: 2 }
              : {
                  minHeight: 280,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }
          }
        >
          {loading ? (
            <LogoLoader size="section" surface="paper" label="Loading the documents" />
          ) : dropZone ? null : (
            <Typography variant="body2" color="text.secondary">
              No documents yet — upload files or select from the library
            </Typography>
          )}
        </Box>
      ) : (
        <List
          dense
          sx={
            dropZone
              ? { mt: 1 }
              : { mt: 1.5, minHeight: 280, maxHeight: 400, overflow: "auto" }
          }
        >
          {documents.map((doc) => (
            <ListItem
              key={doc.id}
              secondaryAction={
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      onClick={() =>
                        documentsApi.downloadDocument(doc.id, doc.filename)
                      }
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove from here (keeps the document in the org library)">
                    <IconButton size="small" onClick={() => handleUnlink(doc)}>
                      <UnlinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                mb: 0.5,
                // Two secondary-action icon buttons need more clearance than
                // MUI's default 48px so the text wraps before reaching them.
                pr: 12,
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 320 }}>
                      {doc.title || doc.filename}
                    </Typography>
                    <Chip
                      label={
                        EXTRACTION_STATUS_LABELS[doc.extraction_status] ||
                        doc.extraction_status
                      }
                      size="small"
                      color={
                        EXTRACTION_CHIP_COLOR[doc.extraction_status] || "default"
                      }
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={`${doc.filename} · ${formatFileSize(doc.size_bytes)}`}
                secondaryTypographyProps={{
                  noWrap: true,
                  title: doc.filename,
                }}
              />
            </ListItem>
          ))}
        </List>
      )}

      <Dialog
        open={pickerOpen}
        onClose={() => !linking && setPickerOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select from Library</DialogTitle>
        <DialogContent dividers sx={{ p: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, title, or description"
            value={librarySearch}
            onChange={(event) => setLibrarySearch(event.target.value)}
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {libraryLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <LogoLoader size="section" surface="paper" label="Loading the library" />
            </Box>
          ) : pickerDocs.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 3, textAlign: "center" }}
            >
              {libraryDocs.length === 0
                ? "Your org library is empty — upload a document first"
                : librarySearch
                  ? "No documents match your search"
                  : "All library documents are already attached here"}
            </Typography>
          ) : (
            <List dense disablePadding sx={{ maxHeight: 360, overflow: "auto" }}>
              {pickerGroups.map((group) => (
                <React.Fragment key={group.label ?? "all"}>
                  {group.label && (
                    <ListSubheader
                      disableSticky
                      sx={{ lineHeight: 2.2, bgcolor: "transparent" }}
                    >
                      {group.label}
                    </ListSubheader>
                  )}
                  {group.docs.map((doc) => (
                    <ListItem key={doc.id} disablePadding>
                      <ListItemButton
                        dense
                        onClick={() => toggleSelected(doc.id)}
                        sx={{ borderRadius: 1 }}
                      >
                        <ListItemIcon sx={{ minWidth: 34 }}>
                          <Checkbox
                            edge="start"
                            size="small"
                            checked={selectedIds.has(doc.id)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={doc.title || doc.filename}
                          secondary={`${doc.filename} · ${formatFileSize(doc.size_bytes)}`}
                          primaryTypographyProps={{
                            variant: "body2",
                            noWrap: true,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)} disabled={linking}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={linkSelected}
            disabled={linking || selectedIds.size === 0}
          >
            {linking
              ? "Adding…"
              : selectedIds.size === 0
                ? "Add documents"
                : `Add ${selectedIds.size} document${
                    selectedIds.size === 1 ? "" : "s"
                  }`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar?.severity || "info"}
          onClose={() => setSnackbar(null)}
          variant="filled"
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentsPanel;
